import { requestSubscription } from 'react-relay';
import type { Disposable, IEnvironment } from 'relay-runtime';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import type { agentSessionEventsSubscription } from './__generated__/agentSessionEventsSubscription.graphql';
import { agentSessionEventsSubscription as subscription } from './agentSessionEventsSubscription';
import { getAgentSessionReconciliation } from './agentSessionReconciliation';
import {
	getAgentSessionsScopeKey,
	type ResolvedAgentSessionsScope,
} from './AgentSessionsScopeProvider';
import { resolveAgentSessionsCloudId } from './resolveAgentSessionsCloudId';

type Change = NonNullable<
	agentSessionEventsSubscription['response']['convoai_onInboxSessionChanged']
>;
export type AgentSessionEvent = Readonly<{
	kind: 'session';
	sequence: number;
	receivedAt: number;
	sessionId: string;
	sessionAri: string;
	session: Change['session'];
}>;
export type AgentSessionSourceEvent =
	| AgentSessionEvent
	| Readonly<{
			kind: 'recovery';
			sequence: number;
			reason: 'online' | 'visible' | 'error';
	  }>;
type Observer = (event: AgentSessionSourceEvent) => void;

export class AgentSessionEventSource {
	private observers = new Set<Observer>();
	private transport: Disposable | undefined;
	private lookup: Disposable | undefined;
	private disposal: ReturnType<typeof setTimeout> | undefined;
	private generation = 0;
	private transportGeneration = 0;
	private sequence = 0;
	private uncertain = true;
	private started = false;
	constructor(
		private environment: IEnvironment,
		private scope: ResolvedAgentSessionsScope,
	) {}
	updateScope(scope: ResolvedAgentSessionsScope): void {
		if (!this.scope.cloudId && scope.cloudId) this.scope = scope;
	}
	get revision(): number {
		return this.sequence;
	}
	get continuous(): boolean {
		return this.started && !this.uncertain;
	}
	private emit(event: AgentSessionSourceEvent): void {
		if (event.kind === 'session') {
			getAgentSessionReconciliation(this.environment, this.scope).event(event);
		}
		for (const observer of this.observers) observer(event);
	}
	private recover = (reason: 'online' | 'visible' | 'error'): void => {
		this.emit({ kind: 'recovery', reason, sequence: ++this.sequence });
	};
	private online = (): void => {
		if (!this.transport) this.connect(this.generation);
		this.recover('online');
	};
	private offline = (): void => {
		this.uncertain = true;
	};
	private visible = (): void => {
		if (document.visibilityState === 'visible' && this.uncertain && navigator.onLine) {
			if (!this.transport) this.connect(this.generation);
			this.recover('visible');
		}
	};
	private connect(generation: number): void {
		const valid = () => this.started && this.generation === generation;
		const start = (cloudId?: string) => {
			if (!valid() || !cloudId || this.transport) return;
			const attempt = ++this.transportGeneration;
			const isCurrent = () => valid() && attempt === this.transportGeneration;
			const variables = {
				accountId: this.scope.accountId,
				workspaceAri: this.scope.workspaceAri,
				cloudId,
			};
			const next = (data: agentSessionEventsSubscription['response'] | null | undefined) => {
				if (!isCurrent()) return;
				// AGG can emit subscription metadata without a domain payload.
				// Transport errors still arrive through onError.
				if (data == null) return;
				const change = data.convoai_onInboxSessionChanged;
				if (
					!change ||
					change.session === undefined ||
					!change.sessionId ||
					!change.sessionAri ||
					(change.session && change.session.id !== change.sessionId)
				) {
					this.recover('error');
					return;
				}
				this.uncertain = false;
				this.emit({
					kind: 'session',
					sequence: ++this.sequence,
					receivedAt: Date.now(),
					sessionId: change.sessionId,
					sessionAri: change.sessionAri,
					session: change.session,
				});
			};
			const error = (exception: unknown) => {
				if (!isCurrent()) return;
				++this.transportGeneration;
				this.uncertain = true;
				const transport = this.transport;
				this.transport = undefined;
				transport?.dispose();
				void logException({ name: 'agentSessionEventSource', exception });
				this.recover('error');
			};
			let payload: agentSessionEventsSubscription['response'] | null | undefined;
			const transport = requestSubscription<agentSessionEventsSubscription>(this.environment, {
				subscription,
				variables,
				cacheConfig: this.scope.enableExplicitAgentSessionSubscriptionSharding
					? {
							metadata: {
								subscriptionShardingContext: {
									type: 'workspace',
									product: 'rovo',
									workspaceId: this.scope.rovoActivationId,
								},
							},
						}
					: undefined,
				updater: (_store, data) => {
					payload = data;
				},
				onNext: () => {
					const data = payload;
					payload = undefined;
					next(data);
				},
				onError: error,
				onCompleted: () => error(new Error('AgentSession subscription completed.')),
			});
			if (isCurrent()) {
				this.transport = transport;
				this.uncertain = !navigator.onLine;
			} else transport.dispose();
		};
		if (this.scope.cloudId) start(this.scope.cloudId);
		else {
			this.lookup?.dispose();
			this.lookup = resolveAgentSessionsCloudId(this.environment, {
				rovoActivationId: this.scope.rovoActivationId,
				onComplete: ({ cloudId }) => {
					if (!valid()) return;
					if (cloudId) start(cloudId);
					else {
						this.uncertain = true;
						this.recover('error');
					}
				},
			});
		}
	}
	observe(observer: Observer): Disposable {
		clearTimeout(this.disposal);
		this.disposal = undefined;
		const listener: Observer = (event) => observer(event);
		this.observers.add(listener);
		if (!this.started && !this.environment.isServer() && typeof window !== 'undefined') {
			this.started = true;
			this.connect(++this.generation);
			window.addEventListener('online', this.online);
			window.addEventListener('offline', this.offline);
			document.addEventListener('visibilitychange', this.visible);
		} else if (this.started && !this.transport && this.uncertain && navigator.onLine) {
			this.connect(this.generation);
		}
		let disposed = false;
		return {
			dispose: () => {
				if (disposed) return;
				disposed = true;
				this.observers.delete(listener);
				if (this.observers.size || this.disposal != null || !this.started) return;
				const generation = this.generation;
				this.disposal = setTimeout(() => {
					this.disposal = undefined;
					if (this.observers.size || generation !== this.generation) return;
					this.started = false;
					this.uncertain = true;
					++this.sequence;
					++this.generation;
					this.transport?.dispose();
					this.transport = undefined;
					this.lookup?.dispose();
					this.lookup = undefined;
					window.removeEventListener('online', this.online);
					window.removeEventListener('offline', this.offline);
					document.removeEventListener('visibilitychange', this.visible);
				}, 1000);
			},
		};
	}
}

const sources = new WeakMap<IEnvironment, Map<string, AgentSessionEventSource>>();
export const getAgentSessionEventSource = (
	environment: IEnvironment,
	scope: ResolvedAgentSessionsScope,
): AgentSessionEventSource => {
	let scopes = sources.get(environment);
	if (!scopes) {
		scopes = new Map();
		sources.set(environment, scopes);
	}
	const key = getAgentSessionsScopeKey(scope);
	let source = scopes.get(key);
	if (!source) {
		source = new AgentSessionEventSource(environment, scope);
		scopes.set(key, source);
	}
	source.updateScope(scope);
	return source;
};
